import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LockIcon, ShieldIcon, BadgeIcon, GlobeIcon, CaretIcon, CheckIcon } from '../icons/Icons';
import { useLanguage } from '../../hooks';
import '../../styles/footer.css';

function Footer() {
  const { t } = useTranslation();
  // The footer selector is the app's primary language controller (LanguageContext →
  // i18next). `languages` is the supported set; changing it switches the whole app in
  // real time and persists to localStorage — no refresh.
  const { language, setLanguage, languages } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);

  const LINKS = [
    { key: 'privacy', label: t('footer.privacy') },
    { key: 'terms', label: t('footer.terms') },
    { key: 'disclosures', label: t('footer.disclosures') },
  ];

  // Close the language menu on outside click or Escape.
  useEffect(() => {
    if (!langOpen) return undefined;
    const onPointer = (e) => {
      if (!e.target.closest('[data-menu-root]')) setLangOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setLangOpen(false);
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  const selectLang = (code) => {
    setLanguage(code);
    setLangOpen(false);
  };

  return (
    <footer className="footer">
      <div className="footer__trust">
        <span className="footer__trust-item">
          <LockIcon /> <span className="footer__trust-label">{t('footer.encrypted')}</span>
        </span>
        <span className="footer__dot" />
        <span className="footer__trust-item">
          <ShieldIcon /> <span className="footer__trust-label">{t('footer.sipcMember')}</span>
        </span>
        <span className="footer__dot" />
        <span className="footer__trust-item">
          <BadgeIcon /> <span className="footer__trust-label">{t('footer.finraRegistered')}</span>
        </span>
      </div>

      <div className="footer__cluster">
        {LINKS.map((link) => (
          <a key={link.key} className="footer__link" href="#/">{link.label}</a>
        ))}
        <span className="footer__dot footer__dot--spaced" />

        <div className="footer__lang" data-menu-root>
          <button
            type="button"
            className={`footer__lang-btn${langOpen ? ' footer__lang-btn--open' : ''}`}
            onClick={() => setLangOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={langOpen}
            aria-label={t('footer.language')}
          >
            <GlobeIcon />
            {language.toUpperCase()}
            <CaretIcon className="footer__lang-caret" />
          </button>
          {langOpen && (
            <div className="menu footer__lang-menu" role="menu">
              {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className="menu__row"
                  onClick={() => selectLang(l.code)}
                >
                  <span>{l.label}</span>
                  {l.code === language && <CheckIcon className="footer__lang-check" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}

export default Footer;
